import { useParams, Link, Navigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, ArrowLeft } from "lucide-react";
import { SERVICES } from "../data/site";
import PageHero from "../components/common/PageHero";
import Reveal from "../components/common/Reveal";
import CTABand from "../components/common/CTABand";
import useSeo from "../hooks/useSeo";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  useSeo({
    title: service ? service.title : "Service",
    description: service ? service.summary : undefined,
  });
  if (!service) return <Navigate to="/services" replace />;
  const Icon = service.icon;
  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <>
      <PageHero overline="Service" title={service.title} description={service.summary} crumb={service.title} parentCrumb={{ label: "Talent Solutions", to: "/services" }} />
      <section className="py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-3 lg:px-8">
          <div className="lg:col-span-2">
            <Reveal>
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-gold/10 text-gold">
                <Icon className="h-7 w-7" />
              </span>
              <p className="mt-6 text-lg leading-relaxed text-charcoal/80">{service.description}</p>
              <h3 className="mt-10 font-serif text-2xl text-navy">What this includes</h3>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {service.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 rounded-xl border border-border bg-white p-4 text-sm text-charcoal">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" /> {p}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={100}>
            <div className="sticky top-28 rounded-2xl border border-border bg-navy p-8 text-white">
              <h4 className="font-serif text-xl">Interested in this service?</h4>
              <p className="mt-3 text-sm text-white/70">Speak with our team about how we can support your requirements.</p>
              <Link to="/employers#request" className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-gold px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-gold-dark">
                Request Talent <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/partnerships#register" className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-white/25 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10">
                Become a Partner
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-2xl text-navy">Other services</h3>
            <Link to="/services" className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-gold">
              <ArrowLeft className="h-4 w-4" /> All services
            </Link>
          </div>
          <div className="mt-8 grid gap-6 sm:grid-cols-3">
            {others.map((s) => {
              const OIcon = s.icon;
              return (
                <Link key={s.slug} to={`/services/${s.slug}`} className="group rounded-2xl border border-border bg-background p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                  <OIcon className="h-6 w-6 text-gold" />
                  <h4 className="mt-4 font-serif text-lg text-navy">{s.title}</h4>
                  <p className="mt-2 text-sm text-muted-foreground">{s.summary}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CTABand
        title={`Ready to activate ${service.title}?`}
        description="Speak with our consultants about how this service can strengthen your recruitment — or explore a partnership built around it."
        primary={{ label: "Request Talent", to: "/employers#request" }}
        secondary={{ label: "Become a Partner", to: "/partnerships#register" }}
      />
    </>
  );
}
